import { djedi } from 'djedi-react'
import { Map } from 'immutable'
import { connectRoutes, redirect, NOT_FOUND } from 'redux-first-router'
import restoreScroll from 'redux-first-router-restore-scroll'
import queryString from 'query-string'
import { select, call, put } from 'redux-saga/effects'

import api from 'api'

import appActions from 'views/App/actions'
import { NAME as ROUTE_HOME } from 'views/Home/constants'
import { NAME as ROUTE_VIEW_ACTIVITY } from 'views/ViewActivity/constants'
import { NAME as ROUTE_FIND } from 'views/FindActivity/constants'
import { NAME as ROUTE_INVITATION } from 'views/Invitation/constants'
import { NAME as ROUTE_LOGIN } from 'views/Login/constants'
import { NAME as ROUTE_RECEIPT } from 'views/Receipt/constants'
import { NAME as ROUTE_SIGNUP_VERIFY } from 'views/SignupVerify/constants'
import { NAME as ROUTE_ACTIVITIES_AND_SCHOOLS } from 'views/ActivitiesAndSchools/constants'
import {
  NAME as ROUTE_CREATE_ACTIVITY,
  EDIT_NAME as ROUTE_EDIT_ACTIVITY,
} from 'views/CreateActivity/constants'
import { NAME as ROUTE_PROFILE } from 'views/Profile/constants'
import {
  PARENT_SIGNUP as ROUTE_PARENT_SIGNUP,
  LEADER_SIGNUP as ROUTE_LEADER_SIGNUP,
} from 'views/Signup/constants'
import {
  NAME as ROUTE_PASSWORD_RESET,
  PASSWORD_RESET_REQUEST as ROUTE_PASSWORD_RESET_REQUEST,
} from 'views/PasswordReset/constants'

import Universal from 'components/Universal'

import { save } from 'state/persist'
import { reverse } from 'utilities/routes'

import { addReducer } from './reducers'
import { runSaga } from './sagas'

const DEFAULT_PAGE_TYPE = 'view'

export const locationSelector = state => state.get('location')

export function* ensureAuthenticated(message) {
  /**
   * Very naive method of testing if user is autneticated,
   * but the only easy way at the moment, as we would need
   * to redo the authentication flow for this to work
   * by simply checking the application state.
   */
  const { pathname: next } = yield select(locationSelector)
  try {
    yield call([api.user, 'getCurrent'])
  } catch (e) {
    const query = { message, next }
    save('next', next)
    yield put(redirect(reverse(ROUTE_LOGIN, { query })))
  }
}

export const ROUTE_LOGOUT = 'logout'
export const staticPages = {
  'fragor-och-svar': 'FAQ',
  kontakt: 'Contact',
  'om-oss': 'AboutUs',
  'sa-funkar-det': 'HowItWorks',
  'allmanna-villkor': 'UserTerms',
  'villkor-for-ansokan': 'ApplicationTerms',
}

const routes = new Map({
  [ROUTE_HOME]: {
    id: ROUTE_HOME,
    path: '/',
    component: 'Home',
  },
  [ROUTE_FIND]: {
    id: ROUTE_FIND,
    path: '/hitta',
    component: 'FindActivity',
    fullWidth: true,
    footerless: true,
    saga: import('views/FindActivity/sagas'),
    reducer: import('views/FindActivity/reducer'),
  },
  [ROUTE_CREATE_ACTIVITY]: {
    id: ROUTE_CREATE_ACTIVITY,
    path: '/aktiviteter/skapa',
    component: 'CreateActivity',
  },
  [ROUTE_EDIT_ACTIVITY]: {
    id: ROUTE_EDIT_ACTIVITY,
    path: '/aktiviteter/:activityId/andra',
    component: 'CreateActivity',
    authRequired: true,
  },
  [ROUTE_RECEIPT]: {
    id: ROUTE_RECEIPT,
    path: '/aktiviteter/:activityId/kvitto',
    component: 'Receipt',
  },
  [ROUTE_VIEW_ACTIVITY]: {
    id: ROUTE_VIEW_ACTIVITY,
    path: '/aktiviteter/:activityId',
    component: 'ViewActivity',
    authRequired: true,
  },
  [ROUTE_PARENT_SIGNUP]: {
    id: ROUTE_PARENT_SIGNUP,
    path: '/bli-medlem',
    component: 'Signup',
  },
  [ROUTE_LEADER_SIGNUP]: {
    id: ROUTE_LEADER_SIGNUP,
    path: '/bli-ledare',
    component: 'Signup',
  },
  [ROUTE_SIGNUP_VERIFY]: {
    id: ROUTE_SIGNUP_VERIFY,
    path: '/registrering/verifera/:token',
    component: 'SignupVerify',
  },
  [ROUTE_LOGIN]: {
    id: ROUTE_LOGIN,
    path: '/logga-in',
    component: 'Login',
  },
  [ROUTE_LOGOUT]: {
    id: ROUTE_LOGOUT,
    path: '/logga-ut',
    component: 'Logout',
  },
  [ROUTE_PROFILE]: {
    id: ROUTE_PROFILE,
    path: '/profil',
    component: 'Profile',
    authRequired: true,
  },
  [ROUTE_INVITATION]: {
    id: ROUTE_INVITATION,
    path: '/inbjudan/:token',
    component: 'Invitation',
  },
  [ROUTE_PASSWORD_RESET_REQUEST]: {
    id: ROUTE_PASSWORD_RESET_REQUEST,
    path: '/nytt-losenord',
    component: 'PasswordReset',
  },
  [ROUTE_PASSWORD_RESET]: {
    id: ROUTE_PASSWORD_RESET,
    path: '/nytt-losenord/:userId/:token',
    component: 'PasswordReset',
  },
  [ROUTE_ACTIVITIES_AND_SCHOOLS]: {
    id: ROUTE_ACTIVITIES_AND_SCHOOLS,
    path: '/aktiviteter-och-skolor',
    component: 'ActivitiesAndSchools',
  },
  [NOT_FOUND]: {
    id: NOT_FOUND,
    path: '/not-found',
    component: 'NotFound',
  },
})
  .merge(
    new Map(
      Object.entries(staticPages).reduce((a, [k, v]) => {
        const b = { ...a }
        b[`static/${k}`] = {
          id: `static/${k}`,
          path: `/${k}`,
          component: v,
          pageType: 'static',
        }
        return b
      }, {}),
    ),
  )
  .map(route => ({
    id: route.id,
    path: route.path,
    component: route.component,
    isLoading: route.isLoading,
    getError: route.getError,
    fullWidth: route.fullWidth,
    pageType: route.pageType || DEFAULT_PAGE_TYPE,
    footerless: route.footerless || false,
    thunk: async dispatch => {
      // Components made using react-universal-component fetch the JS they
      // need synchronously during render on the server, and
      // asynchronously in the browser. In other words, some extra JS will
      // be loaded once this "thunk" is done and the render has begun. But
      // djedi needs to know about all nodes in the to-be-loaded JS _now,_
      // so preload it here, before the `djedi.prefetch()` call. We only
      // have a single universal component that code-splits each view, so
      // the performance should be the same. This makes sure that all
      // nodes are server-side rendered, and avoids "Laddar…" when
      // switching to a new page in the browser.
      await Universal.preload({
        component: route.component,
        pageType: route.pageType || DEFAULT_PAGE_TYPE,
      })

      const [, reducer, saga] = await Promise.all([
        djedi.prefetch(),
        route.reducer || Promise.resolve(),
        route.saga || Promise.resolve(),
      ])

      dispatch(appActions.addDjediNodes(djedi.track()))

      if (reducer) addReducer(route.id, reducer.default)
      if (route.authRequired) await runSaga(ensureAuthenticated)
      if (saga && saga.initialize) await runSaga(saga.initialize)
    },
  }))
  .toJS()

export default history =>
  connectRoutes(routes, {
    location: locationSelector,
    createHistory: () => history,
    querySerializer: queryString,
    restoreScroll: restoreScroll(),
    onAfterChange: (dispatch, getState) => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        const state = getState()
        const { pathname } = locationSelector(state)
        window.dataLayer.push({ event: 'pageview', pageview: pathname })
      }
    },
  })



// WEBPACK FOOTER //
// ../source/state/routes.js